// Export Sort Options explicitly
export const SORT_OPTIONS = [
    { label: 'Price (Low to High)', value: 'price_asc' },
    { label: 'Price (High to Low)', value: 'price_desc' },
    { label: 'Highest ROI', value: 'roi_desc' },
    { label: 'Days on Market (Newest)', value: 'days_on_market_asc' },
    { label: 'Days on Market (Oldest)', value: 'days_on_market_desc' }
];

// Investor Tags for filtering (matches LLM prompt vocabulary)
export const INVESTOR_TAGS = [
    // Tier 1 – Deal Breakers / Access Constraints
    { label: 'Cash Only', value: 'Cash Only' },
    { label: 'As-Is', value: 'As-Is' },
    { label: 'Tenant Occupied', value: 'Tenant Occupied' },
    // Tier 2 – Rehab Severity
    { label: 'Turnkey', value: 'Turnkey' },
    { label: 'Cosmetic Updates', value: 'Cosmetic Updates' },
    { label: 'Fixer Upper', value: 'Fixer Upper' },
    { label: 'Handyman Special', value: 'Handyman Special' },
    { label: 'Full Rehab', value: 'Full Rehab' },
    { label: 'Gut Job', value: 'Gut Job' },
    // Tier 3 – Deal Type
    { label: 'Investor Special', value: 'Investor Special' },
    { label: 'Value-Add', value: 'Value-Add' },
    { label: 'Wholesale Deal', value: 'Wholesale Deal' },
    { label: 'Off-Market', value: 'Off-Market' },
    { label: 'Motivated Seller', value: 'Motivated Seller' },
    // Tier 4 – Sale Context
    { label: 'REO/Bank Owned', value: 'REO/Bank Owned' },
    { label: 'Estate Sale', value: 'Estate Sale' },
    { label: 'Short Sale', value: 'Short Sale' },
    { label: 'Seller Financing', value: 'Seller Financing' },
    // Tier 5 – Property Type
    { label: 'Multi-Family', value: 'Multi-Family' },
    { label: 'ADU Potential', value: 'ADU Potential' }
];

// Feature Keywords for filtering (matches LLM prompt vocabulary)
export const FEATURE_KEYWORDS = [
    // Condition
    { label: 'Dated Kitchen', value: 'Dated Kitchen' },
    { label: 'New Roof', value: 'New Roof' },
    { label: 'Foundation Repair', value: 'Foundation Repair' },
    { label: 'Finished Basement', value: 'Finished Basement' },
    { label: 'Deferred Maintenance', value: 'Deferred Maintenance' },
    { label: 'Fire Damage', value: 'Fire Damage' },
    { label: 'Water Damage', value: 'Water Damage' },
    // Features
    { label: 'Fenced Yard', value: 'Fenced Yard' },
    { label: 'New HVAC', value: 'New HVAC' },
    { label: 'Original Windows', value: 'Original Windows' },
    { label: 'Unfinished Basement', value: 'Unfinished Basement' }
];

// Combined list: Investor Tags first, then Feature Keywords
export const ALL_FILTER_KEYWORDS = [...INVESTOR_TAGS, ...FEATURE_KEYWORDS];

// Investment Type Options for filtering (deterministic, computed from financial data)
export const INVESTMENT_TYPE_OPTIONS = [
    { label: 'Fix & Flip', value: 'Fix & Flip' },
    { label: 'BRRR', value: 'BRRR' },
    { label: 'Long Term Rental', value: 'Long Term Rental' },
    { label: 'Other', value: 'Other' },
];

// Single source of truth for investment type colors
export const INVESTMENT_TYPE_COLORS = {
    'Fix & Flip': '#7c3aed',      // Purple
    'BRRR': '#2563eb',            // Blue
    'Long Term Rental': '#059669', // Green
};

export const FILTER_CONFIG = [
    {
        id: 'location',
        type: 'location_search',
        label: 'Location',
        keys: ['city', 'state', 'zip_code'],
        section: 'main',
        placeholder: 'City, Zip...'
    },
    {
        id: 'price',
        type: 'range',
        label: 'Price',
        minKey: 'min_price',
        maxKey: 'max_price',
        section: 'main'
    },
    {
        id: 'roi',
        type: 'range',
        label: 'ROI %',
        minKey: 'min_roi',
        maxKey: 'max_roi',
        section: 'main'
    },
    {
        id: 'arv',
        type: 'input',
        inputType: 'number',
        label: 'Min ARV',
        key: 'min_arv',
        section: 'main',
        placeholder: 'e.g. 100000'
    },
    {
        id: 'beds',
        type: 'pills',
        label: 'Beds',
        key: 'min_beds',
        group: 'rooms',
        options: [
            { label: 'Any', value: '' },
            { label: '1+', value: '1' },
            { label: '2+', value: '2' },
            { label: '3+', value: '3' },
            { label: '4+', value: '4' }
        ],
        section: 'main'
    },
    {
        id: 'baths',
        type: 'pills',
        label: 'Baths',
        key: 'min_baths',
        group: 'rooms',
        options: [
            { label: 'Any', value: '' },
            { label: '1+', value: '1' },
            { label: '2+', value: '2' },
            { label: '3+', value: '3' }
        ],
        section: 'main'
    },
    {
        id: 'sqft',
        type: 'range',
        label: 'Square Feet',
        minKey: 'min_sqft',
        maxKey: 'max_sqft',
        section: 'main'
    },
    {
        id: 'property_type',
        type: 'checkbox_group',
        label: 'Home Type',
        key: 'property_types',
        options: [
            { label: 'Single Family', value: 'SINGLE_FAMILY' },
            { label: 'Condo', value: 'CONDOS' },
            { label: 'Multi Family', value: 'MULTI_FAMILY' },
            { label: 'Townhouse', value: 'TOWNHOMES' },
            { label: 'Mobile', value: 'MOBILE' },
            { label: 'Land', value: 'LAND' }
        ],
        section: 'main'
    },
    {
        id: 'investment_types',
        type: 'checkbox_group',
        label: 'Investment Type',
        key: 'investment_types',
        options: INVESTMENT_TYPE_OPTIONS,
        section: 'main'
    },
    {
        id: 'days_on_market',
        type: 'input',
        inputType: 'number',
        label: 'Max Days on Market',
        key: 'max_days_on_market',
        section: 'main',
        placeholder: 'e.g. 30'
    },
    {
        id: 'year_built',
        type: 'range',
        label: 'Year Built',
        minKey: 'min_year_built',
        maxKey: 'max_year_built',
        section: 'main'
    },
    {
        id: 'feature_keywords',
        type: 'keyword_input',
        label: 'Tags & Features',
        key: 'feature_keywords',
        options: ALL_FILTER_KEYWORDS,
        section: 'main',
        placeholder: 'Select a tag or feature...'
    },
    // Sort is now handled in SplitView header directly
    {
        id: 'sort',
        type: 'select',
        label: 'Sort By',
        key: 'sort_by',
        options: SORT_OPTIONS,
        section: 'standalone' // Not rendered in FilterBar
    }
];

